import { useState } from 'react';
import { HiOutlinePlus } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';

import { productType } from '@/config';
import { getFileName } from '@/helper';
import GetQuantity from './GetQuantity';
import None from "@/assets/images/default.jpg";
import { translateLanguage } from '@/helper/i18n';
import ProductDetail from './Dialog/ProductDetail';
import { getCurrencyConfig, getShop } from '@/utils/auth';
import { convertToBaseAmount, formatPriceByCurrency } from '@/utils/helper';

interface CardProductProps {
    item: productType;
    handleIncrease: (id: string, qtyStock: number, qty: number, discount?: number,) => void;
    handleDecrease: (id: string, discount: number) => void;
}

export default function CardProduct({ item, handleIncrease, handleDecrease }: CardProductProps) {
    const shop = getShop();
    const { t } = useTranslation();
    const getCurrency = getCurrencyConfig();

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [foodItem, setFoodItem] = useState<productType>();

    const handleOpen = (item: productType) => {
        setFoodItem(item)
        setIsOpen(true);
    }

    const onClose = () => {
        setIsOpen(false);
    }

    if (!shop) return null;

    return (
        <>
            <div className="w-full relative h-full">
                <div className="overflow-hidden cursor-pointer box" onClick={() => handleOpen(item)}>
                    <div className="relative w-full aspect-[4/4]">
                        {item.discountPercent != null && item.discountPercent > 0 && (
                            <div className="absolute top-2 left-2 bg-danger text-white px-2 py-1 rounded-md text-sm font-semibold z-10">
                                -{item.discountPercent}%
                            </div>
                        )}

                        <img
                            alt={item.name || "demo"}
                            src={item.image ? getFileName(item.image) : None}
                            className="w-full h-full object-cover"
                            onError={(e) => (e.currentTarget.src = None)}
                        />

                        {item.isTrackStock && (
                            (item.inventoryStock?.available === 0 || !item.inventoryStock) ||
                            item.alertQty >= item.inventoryStock?.available
                        ) && (
                                <div className="absolute top-0 right-0 w-24 h-16 3xl:w-28 3xl:h-20 pointer-events-none overflow-hidden">
                                    <div className="absolute top-2 -right-11 3xl:top-4 3xl:-right-10 bg-danger text-white text-[8px] font-normal 3xl:text-xs 3xl:font-medium py-1 px-10 transform rotate-45 shadow-md">
                                        {(item.inventoryStock?.available === 0 || !item.inventoryStock)
                                            ? t("outOfStockList")
                                            : t("lowStock")}
                                    </div>
                                </div>
                            )}
                    </div>

                    <div className="flex flex-col justify-between bg-gray-100 p-2 h-full">
                        <div>
                            <div className="text-[14px] 3xl:text-sm font-bold line-clamp-1 text-[#475569]">
                                {translateLanguage(item.name, item.nameKH ? item.nameKH : item.name)}
                            </div>
                            <div className="text-[12px] 3xl:text-xs font-medium line-clamp-1 text-[#475569]">
                                {item.code}
                            </div>
                        </div>

                        <div className="mt-1">
                            {item.discountPercent ? (
                                <div className="flex items-center gap-2">
                                    <span className="text-[12px] 3xl:text-sm font-bold text-danger">
                                        {
                                            formatPriceByCurrency(
                                                convertToBaseAmount(
                                                    item.price * (1 - item.discountPercent / 100),
                                                    getCurrency ? getCurrency.code : "USD",
                                                    // getCurrency?.rateToUSD
                                                ),
                                                getCurrency?.symbol
                                            )
                                        }
                                    </span>
                                    <span className="text-[10px] 3xl:text-xs text-gray-400 line-through">
                                        {
                                            formatPriceByCurrency(
                                                convertToBaseAmount(
                                                    item.price,
                                                    getCurrency ? getCurrency.code : "USD",
                                                    // getCurrency?.rateToUSD
                                                ),
                                                getCurrency?.symbol
                                            )
                                        }
                                    </span>
                                </div>
                            ) : (
                                <div className="text-[12px] 3xl:text-sm font-bold text-danger">
                                    {
                                        formatPriceByCurrency(
                                            convertToBaseAmount(
                                                item.price,
                                                getCurrency ? getCurrency.code : "USD",
                                                // getCurrency?.rateToUSD
                                            ),
                                            getCurrency?.symbol
                                        )
                                    }
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-2 right-3">
                    {
                        item.optionIds.length > 0 ?
                            <>
                                <div className="text-optional text-white py-2 px-3 bg-primary rounded-3xl relative" onClick={() => handleOpen(item)}>
                                    <p
                                        className={`${item.quantity > 0 && "absolute bottom-5 right-0 bg-[#CB4C4C] font-normal text-[10px] text-[#FFFFFF] rounded-full"} ${item.quantity > 9 ? "px-[0.3rem] py-[0.1rem]" : "px-1.5"} absolute z-0 `}
                                    >
                                        {item.quantity > 0 ? item.quantity : ""}
                                    </p>
                                    {t('select')}
                                </div>
                            </> :
                            <>
                                <div className="hidden md:block">
                                    <div
                                        className="bg-white w-[26px] h-[26px] rounded-full flex items-center justify-center shadow-qty text-primary cursor-pointer"
                                        onClick={() =>
                                            handleIncrease(
                                                item._id,
                                                item.isTrackStock ? (item.inventoryStock?.available ?? 0) : 1,
                                                item.isTrackStock ? (item.quantity ? item.quantity : 0) : 0,
                                                item.discountPercent
                                            )
                                        }
                                    >
                                        <HiOutlinePlus size={18} />
                                    </div>
                                </div>
                                <div className="block md:hidden">
                                    {
                                        item.quantity ?
                                            <>
                                                <GetQuantity
                                                    isWaiter={item.isWaiter}
                                                    qty={item.quantity}
                                                    handleDecrease={() => handleDecrease(item._id, item.discountPercent)}
                                                    handleIncrease={() =>
                                                        handleIncrease(
                                                            item._id,
                                                            item.isTrackStock ? (item.inventoryStock?.available ?? 0) : 1,
                                                            item.isTrackStock ? item.quantity : 0,
                                                            item.discountPercent
                                                        )
                                                    }
                                                />
                                            </>
                                            :
                                            <div
                                                className="bg-white w-[26px] h-[26px] rounded-full flex items-center justify-center shadow-qty text-primary cursor-pointer"
                                                onClick={() =>
                                                    handleIncrease(
                                                        item._id,
                                                        item.isTrackStock ? (item.inventoryStock?.available ?? 0) : 1,
                                                        item.isTrackStock ? item.quantity : 0,
                                                        item.discountPercent
                                                    )
                                                }
                                            >
                                                <HiOutlinePlus size={18} />
                                            </div>
                                    }
                                </div>
                            </>
                    }
                </div>
            </div>
            <ProductDetail isOpen={isOpen} onClose={onClose} foodItem={foodItem} />
        </>
    )
}
